import { memo, useContext, useState } from 'react';

import RenameModal from '@atom/components/common/RenameModal';
import folderMoveIcon from '@atom/components/common/svgIcons/folderMove.svg';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { Checkbox, Icon, ListTable, Menu } from '@atom/mui';
import {
  CreateNewFolderIcon,
  DeleteIcon,
  FileDownloadIcon,
} from '@atom/mui/icons';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { downloadFile } from '@atom/utilities/fileUtilities';
import { getMediaIcon } from '@atom/utilities/iconUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';

import ConfirmDeleteModal from './ConfirmDeleteModal';
import { ROOT_FOLDER_ID } from './constants';
import CreateMediaFolderModal from './CreateMediaFolderModal';
import EditMediaFolderModal from './EditMediaFolderModal';
import FilesTabContext from './FilesTabContext';
import MoveMediaFolderModal from './MoveMediaFolderModal';
import { MediaRow, RowModalType } from './types';

import classes from './filesTab.module.css';

const { TableRow, TableCell } = ListTable;
const { MenuItem } = Menu;

const styles = {
  cell: { padding: '0 0 0 1.5rem' },
  cellRight: { padding: '0 1.5rem 0 0' },
};

type MediaTableRowProps = {
  row: MediaRow;
  isFolder: boolean;
  checked: boolean;
  showFolderCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onToggleCheck: (id: string) => void;
  onOpenFolder: (id: string) => void;
};

const MediaTableRow = ({
  row,
  isFolder,
  checked,
  showFolderCreate,
  canUpdate,
  canDelete,
  onToggleCheck,
  onOpenFolder,
}: MediaTableRowProps) => {
  const { createFolder, editFolder, deleteFolder, renameMedia, deleteMedia } =
    useContext(FilesTabContext);

  const [modal, setModal] = useState<RowModalType>(RowModalType.NONE);

  const handleDownload = () =>
    downloadFile(
      `${FILES_ENDPOINT}/${row.fileId}`,
      `${row.name}.${row.fileExtension}`,
    );

  const extension = row.fileId ? row.fileExtension : 'folder';
  const ancestors = row.ancestors ?? [];
  const parentFolderId =
    ancestors.length > 0 ? ancestors[ancestors.length - 1] : ROOT_FOLDER_ID;

  const nameLink = isFolder ? (
    <div
      className={classes.clickableLink}
      onClick={() => onOpenFolder(row.id)}
      data-cy={`filesTabFolderLink-${row.name}`}
    >
      {row.name}
    </div>
  ) : (
    <a
      className={classes.clickableLink}
      href={`${FILES_ENDPOINT}/${row.fileId}?filename=${encodeURIComponent(`${row.name}.${extension}`)}`}
      target="_blank"
      rel="noreferrer"
      data-cy={`filesTabFileLink-${row.name}`}
    >{`${row.name}.${extension}`}</a>
  );

  const folderMenu = (
    <Menu dataCyLabel={`filesTabFolderMenu-${row.name}`}>
      {canUpdate && (
        <MenuItem
          startAdornment={
            <Icon>
              <img src={renameIcon} />
            </Icon>
          }
          onClick={() => setModal(RowModalType.EDIT_FOLDER)}
          data-cy={`filesTabFolderRename-${row.name}`}
        >
          Rename
        </MenuItem>
      )}
      {showFolderCreate && (
        <MenuItem
          startAdornment={<Icon icon={CreateNewFolderIcon} />}
          onClick={() => setModal(RowModalType.CREATE_SUB_FOLDER)}
          data-cy={`filesTabFolderCreateSubfolder-${row.name}`}
        >
          Create Folder
        </MenuItem>
      )}
      {canUpdate && (
        <MenuItem
          startAdornment={
            <Icon>
              <img src={folderMoveIcon} />
            </Icon>
          }
          onClick={() => setModal(RowModalType.MOVE_FOLDER)}
          data-cy={`filesTabFolderMove-${row.name}`}
        >
          Move to Folder
        </MenuItem>
      )}
      {canDelete && (
        <MenuItem
          startAdornment={<Icon icon={DeleteIcon} />}
          onClick={() => setModal(RowModalType.DELETE_FOLDER)}
          data-cy={`filesTabFolderDelete-${row.name}`}
        >
          Delete
        </MenuItem>
      )}
    </Menu>
  );

  const fileMenu = (
    <Menu dataCyLabel={`filesTabFileMenu-${row.name}`}>
      {canUpdate && (
        <MenuItem
          startAdornment={
            <Icon>
              <img src={renameIcon} />
            </Icon>
          }
          onClick={() => setModal(RowModalType.EDIT_FILE)}
          data-cy={`filesTabFileRename-${row.name}`}
        >
          Rename
        </MenuItem>
      )}
      <MenuItem
        startAdornment={<Icon icon={FileDownloadIcon} />}
        onClick={handleDownload}
        data-cy={`filesTabFileDownload-${row.name}`}
      >
        Download
      </MenuItem>
      {canDelete && (
        <MenuItem
          startAdornment={<Icon icon={DeleteIcon} />}
          onClick={() => setModal(RowModalType.DELETE_FILE)}
          data-cy={`filesTabFileDelete-${row.name}`}
        >
          Delete
        </MenuItem>
      )}
    </Menu>
  );

  const showFolderMenu =
    isFolder && (canUpdate || canDelete || showFolderCreate);
  const showFileMenu = !isFolder;

  return (
    <TableRow>
      <TableCell width="2rem" style={styles.cell}>
        {!isFolder && (
          <Checkbox
            checked={checked}
            onChange={() => onToggleCheck(row.id)}
            data-cy={`filesTabRowCheckbox-${row.name}`}
          />
        )}
      </TableCell>
      <TableCell style={styles.cell}>
        <div className={classes.nameRowContainer}>
          <div className={classes.nameRowIcon}>
            {getMediaIcon(row.type, extension, row.fileId, row.name)}
          </div>
          {nameLink}
        </div>
      </TableCell>
      <TableCell style={styles.cell}>{row.fileExtension}</TableCell>
      <TableCell style={styles.cell}>
        {row.createdBy && typeof row.createdBy === 'object'
          ? getUserFullName(row.createdBy)
          : '-'}
      </TableCell>
      <TableCell style={styles.cell}>
        {setDisplayDate(row.createdDate)}
      </TableCell>
      <TableCell align="right" width="3rem" style={styles.cellRight}>
        {showFolderMenu && folderMenu}
        {showFileMenu && fileMenu}
      </TableCell>

      <EditMediaFolderModal
        open={modal === RowModalType.EDIT_FOLDER}
        folderId={row.id}
        initialName={row.name}
        onEdit={editFolder}
        closeModal={() => setModal(RowModalType.NONE)}
      />
      <CreateMediaFolderModal
        open={modal === RowModalType.CREATE_SUB_FOLDER}
        parentFolderId={row.id}
        onCreate={createFolder}
        closeModal={() => setModal(RowModalType.NONE)}
      />
      <MoveMediaFolderModal
        open={modal === RowModalType.MOVE_FOLDER}
        type="folder"
        ids={[row.id]}
        closeModal={() => setModal(RowModalType.NONE)}
        disabled={[row.id, parentFolderId]}
      />
      <ConfirmDeleteModal
        open={modal === RowModalType.DELETE_FOLDER}
        title="Delete Folder"
        onCancel={() => setModal(RowModalType.NONE)}
        onConfirm={() => {
          deleteFolder(row.id);
          setModal(RowModalType.NONE);
        }}
        CancelButtonProps={{
          'data-cy': `filesTabFolderDeleteCancel-${row.name}`,
        }}
        ConfirmButtonProps={{
          'data-cy': `filesTabFolderDeleteConfirm-${row.name}`,
        }}
      >
        Are you sure you want to delete this folder? If the folder has any
        subfolders or files saved below this folder, everything will be deleted.
      </ConfirmDeleteModal>
      <RenameModal
        open={modal === RowModalType.EDIT_FILE}
        id={row.id}
        name={row.name}
        type={row.type}
        renameAction={renameMedia}
        closeModal={() => setModal(RowModalType.NONE)}
      />
      <ConfirmDeleteModal
        open={modal === RowModalType.DELETE_FILE}
        title={`Delete ${textDisplayUtilities.capitalize(row.type)}s`}
        onCancel={() => setModal(RowModalType.NONE)}
        onConfirm={() => {
          deleteMedia(row.id);
          setModal(RowModalType.NONE);
        }}
        CancelButtonProps={{
          'data-cy': `filesTabFileDeleteCancel-${row.name}`,
        }}
        ConfirmButtonProps={{
          'data-cy': `filesTabFileDeleteConfirm-${row.name}`,
        }}
      >
        {`Are you sure you want to delete this ${row.type}?`}
      </ConfirmDeleteModal>
    </TableRow>
  );
};

export default memo(MediaTableRow);
